import { useLingui } from "@lingui/react/macro";

import React, { Suspense, useCallback } from "react";
import { Controller, ControllerProps, useFormContext } from "react-hook-form";
import { useGeolocation } from "src/utils/localization/geolocation/GeolocationContext";
import InputWithError from "./Form/InputWithError";
import { isPresent } from "src/utils/checks";
import ErrorBoundary from "./ErrorBoundary";

const PhoneInput = React.lazy(() => import("./PhoneInput"));

export interface PhoneInputControlProps {
  name: string;
  defaultValue?: unknown;
  id: string;
  required: boolean;
}

const PhoneInputControl: React.FC<
  React.PropsWithChildren<PhoneInputControlProps>
> = ({ name, defaultValue = "", id, required = false }) => {
  const geolocation = useGeolocation();
  const formMethods = useFormContext();
  const { t } = useLingui();
  const { control } = formMethods;

  const labelPostfix = required ? " *" : ` (${t`optional`})`;
  const placeholder = `${t`Form Phone`}${labelPostfix}`;

  const phoneInputRender: ControllerProps["render"] = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (props: any) => (
      <ErrorBoundary>
        <InputWithError
          id={id}
          name={name}
          label={placeholder}
          forceLabelRenderAsFocused
        >
          <Suspense>
            <PhoneInput
              country={geolocation?.countryCode}
              placeholder={placeholder}
              {...(props["field"] || props)}
            />
          </Suspense>
        </InputWithError>
      </ErrorBoundary>
    ),
    [geolocation?.countryCode, id, labelPostfix, name, placeholder]
  );

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      rules={{
        required: required ? t({ id: `form-required-phone-number` }) : false,
        validate: async (value) => {
          const { isPossiblePhoneNumber } = await import(
            /* webpackChunkName: "isPossiblePhoneNumber" */
            /* webpackMode: "lazy" */
            /* webpackPreload: false */
            /* webpackExports: ["isPossiblePhoneNumber"] */
            "react-phone-number-input/min"
          );

          if ((!isPresent(value) || value === "") && !required) return true;

          if (isPresent(value) && !isPossiblePhoneNumber(value))
            return t({ id: `form-invalid-phone-number` });
          return true;
        }
      }}
      render={phoneInputRender}
    />
  );
};

export default PhoneInputControl;
